// ============================================
// FILE: src/components/CategoriesScreen.jsx
// ============================================
//
// The bottom menu's "Categories" tab (mobile): every category in one place,
// top-level categories as cards with their sub-categories listed beneath.
// Tapping either opens the same category page the home screen does.

import React, { useMemo } from 'react';
import { ArrowLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import { categoryIcons } from '../data/products';
import { resolveProductImageUrl } from '../utils/imageHelpers';
import { isCategoryVisible } from '../utils/categoryConfig';

const idOf = (v) => (v === null || v === undefined || v === '' ? null : String(v));

const CategoryThumb = ({ category, size = 56 }) => {
  const img = resolveProductImageUrl(category.image || category.image_url);
  return (
    <div
      className="relative flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center bg-[#fff0f4]"
      style={{ width: size, height: size, border: '2px solid #ffd6e0' }}
    >
      <span className="leading-none" style={{ fontSize: size * 0.45 }} aria-hidden="true">
        {categoryIcons[category.name] || '📦'}
      </span>
      {img && (
        <img
          src={img}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      )}
    </div>
  );
};

const CategoriesScreen = ({
  categories = [],
  products = [],
  onCategoryClick,
  onBack,
}) => {
  // Parents with their visible children; a child whose parent is hidden or
  // missing is promoted to the top level so it stays reachable.
  const groups = useMemo(() => {
    const list = (Array.isArray(categories) ? categories : []).filter((c) => isCategoryVisible(c?.name));
    const ids = new Set(list.map((c) => idOf(c.id)));
    const top = list.filter((c) => !idOf(c.parent_id) || !ids.has(idOf(c.parent_id)));
    return top.map((parent) => ({
      parent,
      children: list.filter((c) => idOf(c.parent_id) === idOf(parent.id)),
    }));
  }, [categories]);

  // Product counts per category id, so each row can say how much is inside.
  const counts = useMemo(() => {
    const map = new Map();
    (Array.isArray(products) ? products : []).forEach((p) => {
      const id = idOf(p?.category_id ?? (typeof p?.category === 'object' ? p?.category?.id : null));
      if (id) map.set(id, (map.get(id) || 0) + 1);
    });
    return map;
  }, [products]);

  const countFor = (parent, children) => (
    (counts.get(idOf(parent.id)) || 0)
    + children.reduce((n, c) => n + (counts.get(idOf(c.id)) || 0), 0)
  );

  const open = (category) => onCategoryClick && onCategoryClick(category);

  return (
    <div className="pb-24" style={{ background: '#f7f7f7', minHeight: '100vh', color: '#1b1b1b' }}>
      <div
        className="sticky top-0 z-20 bg-white flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: '1px solid #f0f0f0', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        {onBack && (
          <button type="button" onClick={onBack} aria-label="Back" className="p-1 -ml-1 rounded-full hover:bg-gray-100">
            <ArrowLeft size={20} className="text-[#282c3f]" />
          </button>
        )}
        <h1 className="text-lg font-bold text-[#282c3f] flex-1">Categories</h1>
        <span className="text-xs text-[#94969f]">{groups.length} {groups.length === 1 ? 'category' : 'categories'}</span>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-20 px-6 text-[#94969f]">
          <LayoutGrid size={44} className="mx-auto mb-3 opacity-40" />
          <p className="font-medium">No categories yet</p>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {groups.map(({ parent, children }) => {
            const total = countFor(parent, children);
            return (
              <div key={parent.id} className="bg-white rounded shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => open(parent)}
                  className="w-full flex items-center gap-3 p-3 text-left active:bg-gray-50"
                >
                  <CategoryThumb category={parent} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#282c3f] truncate">{parent.name}</p>
                    <p className="text-xs text-[#94969f]">
                      {total} {total === 1 ? 'product' : 'products'}
                      {children.length > 0 && ` · ${children.length} sub-categories`}
                    </p>
                  </div>
                  <ChevronRight size={18} className="text-[#94969f] flex-shrink-0" />
                </button>

                {children.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-3 pb-3">
                    {children.map((child) => (
                      <button
                        key={child.id}
                        type="button"
                        onClick={() => open(child)}
                        className="flex items-center gap-1.5 max-w-full px-3 py-1.5 rounded-full text-xs font-medium text-[#535766] hover:bg-[#fff8fa]"
                        style={{ border: '1.5px solid #e8e8e8' }}
                      >
                        <span className="truncate">{child.name}</span>
                        {counts.get(idOf(child.id)) > 0 && (
                          <span className="text-[10px] text-[#94969f]">{counts.get(idOf(child.id))}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CategoriesScreen;
